import React, { useState, useEffect } from 'react';
import { Button, Card, FormGroup, MenuItem, Dialog, Classes, Icon, Tooltip, Position, Switch, NumericInput } from '@blueprintjs/core';
import { Select } from "@blueprintjs/select";
import { NotificacaoInterna } from '../../../Services/Notification';
import api from '../../../Services/node';
import { Objeto } from '../../../Services/Models';
import './style.css';

// Interface para links públicos diretos
interface PublicDirectLink {
  id: string;
  empresaMae: string;
  obra: string;
  empresa: string;
  nome: string;
  createdAt: number;
  createdBy: string;
  url: string;
}

// Função para formatar datas no formato brasileiro
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

interface LinkManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const PublicLinkManager: React.FC<LinkManagerProps> = ({ isOpen, onClose }): React.ReactElement => {
  // Estado para empresas e obras disponíveis
  const [empresasMae, setEmpresasMae] = useState<string[]>([]);
  const [selectedEmpresaMae, setSelectedEmpresaMae] = useState<string>('');
  const [empreendimentos, setEmpreendimentos] = useState<Array<Objeto.CentroDeCusto>>([]);
  const [selectedEmpreendimento, setSelectedEmpreendimento] = useState<Objeto.CentroDeCusto | null>(null);
  
  // Estado para configurações do link
  const [expirationEnabled, setExpirationEnabled] = useState<boolean>(false);
  const [expirationDays, setExpirationDays] = useState<number>(7);
  const [isGeneratingLink, setIsGeneratingLink] = useState<boolean>(false);
  const [lastGeneratedLink, setLastGeneratedLink] = useState<string>("");
  
  // Estado para links existentes
  const [publicLinks, setPublicLinks] = useState<any[]>([]);
  
  const PickerEmpreendimento = Select.ofType<Objeto.CentroDeCusto>();

  // Carregar empresas disponíveis
  useEffect(() => {
    const loadCompanies = async (): Promise<void> => {
      try {
        // Esta é uma lista simulada - você precisará adaptar para sua API real
        setEmpresasMae(['Rio', 'CasaETerra', 'Cosentino', 'BP8', 'Espaco']);
      } catch (error) {
        console.error('Erro ao carregar empresas:', error);
      }
    };
    
    if (isOpen) {
      loadCompanies();
      loadPublicLinks();
    }
  }, [isOpen]);
  
  // Carregar empreendimentos quando uma empresa é selecionada
  useEffect(() => {
    const loadEmpreendimentos = async (): Promise<void> => {
      if (!selectedEmpresaMae) return;
      
      try {
        const response = await api.get(`/Mapas/Empreendimentos?empresaMae=${selectedEmpresaMae}`);
        if (response.data && response.data.empreendimentos) {
          setEmpreendimentos(response.data.empreendimentos);
        }
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error);
        NotificacaoInterna.ExibirNotificacao(
          "Erro",
          "Não foi possível carregar a lista de empreendimentos.",
          NotificacaoInterna.TipoDeNotificacao.Erro
        );
      }
    };
    
    loadEmpreendimentos();
  }, [selectedEmpresaMae]);
  
  // Carregar links públicos existentes
  const loadPublicLinks = (): void => {
    try {
      // Carregar links diretos do localStorage
      const savedLinks = JSON.parse(localStorage.getItem('publicMapDirectLinks') || '[]');
      
      // Ordenar por data de criação (mais recentes primeiro)
      savedLinks.sort((a: PublicDirectLink, b: PublicDirectLink) => b.createdAt - a.createdAt);
      
      setPublicLinks(savedLinks);
    } catch (error) {
      console.error('Erro ao carregar links públicos:', error);
    }
  };
  
  // Gerar um novo link público
  const handleGenerateLink = async () => {
    if (!selectedEmpreendimento) return;
    
    try {
      setIsGeneratingLink(true);
      
      // Construir a URL direta para o mapa público
      const empresaMae = selectedEmpresaMae || '';
      const obra = selectedEmpreendimento.sigla;
      const publicMapUrl = `${window.location.origin}/public/map?empresaMae=${encodeURIComponent(empresaMae)}&obra=${encodeURIComponent(obra)}`;
      setLastGeneratedLink(publicMapUrl);
      
      // Adicionar o link à lista de links públicos no localStorage para histórico
      const newLink = {
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        empresaMae,
        obra,
        empresa: selectedEmpreendimento.empresa,
        nome: selectedEmpreendimento.descricao,
        createdAt: Date.now(),
        createdBy: 'usuário atual',
        url: publicMapUrl
      };
      
      const savedLinks = JSON.parse(localStorage.getItem('publicMapDirectLinks') || '[]');
      savedLinks.push(newLink);
      localStorage.setItem('publicMapDirectLinks', JSON.stringify(savedLinks));
      
      // Atualizar a lista de links
      loadPublicLinks();
      
      // Mostrar notificação de sucesso
      NotificacaoInterna.ExibirNotificacao(
        "Link gerado com sucesso!",
        "O link foi gerado com sucesso.",
        NotificacaoInterna.TipoDeNotificacao.Sucesso
      );
      
      // Recarregar a lista de links
      loadPublicLinks();
      
      // Não resetamos a seleção para permitir gerar vários links para o mesmo empreendimento
    } catch (error) {
      console.error('Erro ao gerar link público:', error);
      NotificacaoInterna.ExibirNotificacao(
        "Erro",
        "Não foi possível gerar o link público. Tente novamente.",
        NotificacaoInterna.TipoDeNotificacao.Erro
      );
    } finally {
      setIsGeneratingLink(false);
    }
  };
  
  // Copiar um link existente para a área de transferência
  const handleCopyLink = async (url: string): Promise<void> => {
    try {
      // Copiar o link para a área de transferência
      await navigator.clipboard.writeText(url);
      console.log('Link copiado para a área de transferência:', url);
      
      NotificacaoInterna.ExibirNotificacao(
        "Link copiado!",
        "O link foi copiado para a área de transferência.",
        NotificacaoInterna.TipoDeNotificacao.Sucesso
      );
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      
      NotificacaoInterna.ExibirNotificacao(
        "Erro",
        "Não foi possível copiar o link.",
        NotificacaoInterna.TipoDeNotificacao.Erro
      );
    }
  };

  const handleOpenLink = (url: string) => {
    window.open(url, '_blank');
  };

  const handleRevokeLink = (id: string): void => {
    try {
      // Remover o link do localStorage
      const savedLinks = JSON.parse(localStorage.getItem('publicMapDirectLinks') || '[]');
      const updatedLinks = savedLinks.filter((link: PublicDirectLink) => link.id !== id);
      localStorage.setItem('publicMapDirectLinks', JSON.stringify(updatedLinks));
      
      NotificacaoInterna.ExibirNotificacao(
        "Link removido",
        "O link público foi removido com sucesso.",
        NotificacaoInterna.TipoDeNotificacao.Sucesso
      );
      
      loadPublicLinks();
    } catch (error) {
      console.error('Erro ao remover link:', error);
      
      NotificacaoInterna.ExibirNotificacao(
        "Erro",
        "Não foi possível remover o link.",
        NotificacaoInterna.TipoDeNotificacao.Erro
      );
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Gerenciador de Links Públicos"
      className="public-link-manager-dialog"
    >
      <div className={Classes.DIALOG_BODY}>
        <div className="public-link-manager-container">
          <Card className="public-link-form">
            <h3>Gerar novo link público</h3>
            
            <FormGroup label="Empresa" labelFor="empresa-select">
              <Select
                items={empresasMae}
                itemRenderer={(item, { handleClick }) => (
                  <MenuItem 
                    key={item} 
                    text={item} 
                    onClick={handleClick} 
                  />
                )}
                onItemSelect={(item) => setSelectedEmpresaMae(item)}
                filterable={false}
                popoverProps={{ minimal: true }}
              >
                <Button 
                  text={selectedEmpresaMae || "Selecione uma empresa"} 
                  rightIcon="caret-down"
                  fill
                />
              </Select>
            </FormGroup>
            
            <FormGroup
              label="Empreendimento"
              labelFor="empreendimento-select"
              helperText="Selecione o empreendimento para o qual deseja gerar um link público"
            >
              <PickerEmpreendimento
                items={empreendimentos}
                itemRenderer={(item, { handleClick }) => (
                  <MenuItem 
                    key={item.sigla} 
                    text={item.descricao} 
                    onClick={handleClick} 
                  />
                )}
                onItemSelect={(item) => setSelectedEmpreendimento(item)}
                itemPredicate={(query, item) => 
                  item.descricao.toLowerCase().includes(query.toLowerCase())
                }
                inputProps={{ id: "empreendimento-select" }}
                noResults={<MenuItem disabled text="Nenhum empreendimento encontrado" />}
                popoverProps={{ minimal: true, usePortal: true, popoverClassName: "empreendimento-popover" }}
              >
                <Button
                  text={selectedEmpreendimento ? selectedEmpreendimento.descricao : "Selecione um empreendimento"}
                  rightIcon="caret-down"
                  fill
                />
              </PickerEmpreendimento>
            </FormGroup>
            
            <FormGroup
              label="Configurações de expiração"
              labelFor="expiration-switch"
            >
              <div className="expiration-config">
                <Switch
                  id="expiration-switch"
                  label="Definir data de expiração"
                  checked={expirationEnabled}
                  onChange={() => setExpirationEnabled(!expirationEnabled)}
                />
                
                {expirationEnabled && (
                  <div className="expiration-input">
                    <NumericInput
                      min={1}
                      max={365}
                      value={expirationDays}
                      onValueChange={(value) => setExpirationDays(value)}
                    />
                    <span>dias</span>
                  </div>
                )}
              </div>
            </FormGroup>
            
            <div className="action-buttons-container">
              <Button
                type="submit"
                intent="primary"
                text="Gerar Link"
                disabled={!selectedEmpreendimento || isGeneratingLink}
                loading={isGeneratingLink}
                onClick={handleGenerateLink}
              />
              {lastGeneratedLink && (
                <Button
                  icon="share"
                  intent="success"
                  text="Abrir Último Link"
                  onClick={() => window.open(lastGeneratedLink, '_blank')}
                />
              )}
            </div>
          </Card>
          
          <div className="public-links-list">
            <h3>Links públicos existentes</h3>
            
            {publicLinks.length === 0 ? (
              <div className="no-links">
                <Icon icon="info-sign" size={20} />
                <p>Nenhum link público encontrado</p>
              </div>
            ) : (
              <div className="links-table">
                <table>
                  <thead>
                    <tr>
                      <th>Empreendimento</th>
                      <th>Data de criação</th>
                      <th>Expira em</th>
                      <th>Criado por</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publicLinks.map((link) => {
                      const createdDate = new Date(link.createdAt);
                      let expirationInfo = "Nunca";
                      
                      if (link.expiresAt) {
                        const expirationDate = new Date(link.expiresAt);
                        const now = new Date();
                        
                        if (expirationDate < now) {
                          expirationInfo = "Expirado";
                        } else {
                          const diffTime = Math.abs(expirationDate.getTime() - now.getTime());
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          expirationInfo = `${diffDays} dia(s)`;
                        }
                      }
                      
                      return (
                        <tr key={link.id}>
                          <td>{link.nome}</td>
                          <td>{formatDate(createdDate)}</td>
                          <td>{expirationInfo}</td>
                          <td>{link.createdBy}</td>
                          <td className="link-actions">
                        <div className="action-buttons-container">
                          <Tooltip content="Copiar link" position={Position.TOP}>
                            <Button
                              icon="duplicate"
                              small
                              minimal
                              onClick={() => handleCopyLink(link.url)}
                            />
                          </Tooltip>
                          <Tooltip content="Abrir em nova aba" position={Position.TOP}>
                            <Button
                              icon="share"
                              small
                              minimal
                              intent="primary"
                              onClick={() => handleOpenLink(link.url)}
                            />
                          </Tooltip>
                          <Tooltip content="Remover link" position={Position.TOP}>
                            <Button
                              icon="trash"
                              small
                              minimal
                              intent="danger"
                              onClick={() => handleRevokeLink(link.id)}
                            />
                          </Tooltip>
                        </div>
                      </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button text="Fechar" onClick={onClose} />
        </div>
      </div>
    </Dialog>
  );
};

export default PublicLinkManager;
